import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { FiPaperclip, FiDownload, FiEdit } from 'react-icons/fi';
import { IoPersonCircle } from 'react-icons/io5';
import { FiExternalLink } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import toast from 'react-hot-toast';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { FiMaximize, FiMinimize } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import AIEmailView from './AIEmailView';
import { DataState } from '../../context/DataProvider';
import {
  getOutlookMessageDetails,
  getGmailMessageDetails,
  getOutlookEmailDetailsFromDB,
  getGmailEmailDetailsFromDB,
  API_URL,
} from '../../services/api';
import EmailIframe from './EmailIframe';
import { getFileIcon } from './AttachmentIcon';

const handleDownload = async (
  messageId,
  attachmentId,
  fileName,
  accessToken,
  isOutlook,
) => {
  try {
    // Make a GET request to the FastAPI endpoint with the access_token in Authorization header
    let response = null;
    if (isOutlook) {
      response = await fetch(
        `${API_URL}/email/outlook/${messageId}/download-attachment/${attachmentId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`, // Adding Bearer token in the header
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch attachment');
      }
    } else {
      response = await fetch(
        `${API_URL}/email/gmail/${messageId}/download-attachment/${attachmentId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`, // Adding Bearer token in the header
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch attachment');
      }
    }

    // The file is streamed as a Blob
    const blob = await response.blob(); // Convert the streamed content to Blob

    // Create an object URL for the Blob to trigger the download
    const url = window.URL.createObjectURL(blob);
    // Create an anchor element and programmatically click to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName; // Customize the filename
    document.body.appendChild(link); // Append link to the body (required for Firefox)
    link.click();
    document.body.removeChild(link); // Clean up link after download
    window.URL.revokeObjectURL(url); // Revoke the URL to free memory
    toast.success('Download Successful:', fileName);
  } catch (error) {
    toast.error(
      typeof error === 'string'
        ? error
        : error?.message || error?.detail || 'Download Failed',
    );
  }
};

const AttachmentItem = ({ messageId, attachment, isOutlook }) => {
  const { userGoogleId } = DataState();
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadClick = async () => {
    if (isDownloading) return; // Prevent double clicks
    setIsDownloading(true);

    try {
      await handleDownload(
        messageId,
        attachment.attachmentId,
        attachment.filename,
        userGoogleId,
        isOutlook,
      );
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : error?.message || error?.detail || 'Download Failed',
      );
    } finally {
      setIsDownloading(false); // Reset downloading state
    }
  };

  return (
    <div
      className="inline-flex h-10 w-40 cursor-pointer items-center justify-center rounded-lg border p-2 py-3 text-gray-500 shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered ? (
        <div className="flex w-full items-center justify-center gap-4">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-gray-600"
              size={16}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-gray-600 hover:text-black"
              size={16}
            />
          )}

          <FiPaperclip
            className="cursor-pointer text-gray-600 hover:text-black"
            size={16}
          />
          <FiEdit
            className="cursor-pointer text-gray-600 hover:text-black"
            size={16}
          />
        </div>
      ) : (
        <div className="flex w-full items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center">
            {getFileIcon(attachment.filename)} {/* Handle both cases */}
          </div>
          <span className="truncate text-sm font-medium">
            {attachment.filename || attachment.name} {/* Handle both cases */}
          </span>
        </div>
      )}
    </div>
  );
};

function convertToIST(dateString) {
  // Parse the date string into a Date object (in UTC)
  const dateUTC = new Date(dateString);

  // Format the date to "28 Jan, 2025"
  const formattedDate = dateUTC.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  // Format the time to "7:20 AM"
  const formattedTime = dateUTC.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return { date: formattedDate, time: formattedTime };
}

function parseEmailList(emailString) {
  const emailObjects = emailString
    .split(/,\s*/)
    .map((entry) => {
      // Match cases with quotes for the email: `"email@example.com" <email@example.com>`
      const quotedEmailMatch = entry.match(/^"([^"]+)"\s*<([^>]+)>$/);
      if (quotedEmailMatch) {
        const email = quotedEmailMatch[2];
        return { name: quotedEmailMatch[1], email };
      }

      // Match cases with name and email: `Name <email@example.com>`
      const nameEmailMatch = entry.match(/^(.+?)\s*<([^>]+)>$/);
      if (nameEmailMatch) {
        const name = nameEmailMatch[1].trim();
        const email = nameEmailMatch[2].trim();
        return { name, email };
      }

      return { name: entry.trim(), email: entry.trim() }; // Handle unexpected formats gracefully
    })
    .filter(Boolean); // Remove invalid entries

  return emailObjects;
}

const SkeletonLoader = ({ type = 'text' }) => {
  if (type === 'text') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-6 w-full animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }
  if (type === 'emailbody') {
    return (
      <div className="mr-4 space-y-4">
        <div className="h-6 w-full animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-1/2 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }

  if (type === 'image') {
    return (
      <div className="mr-4 h-48 w-full animate-pulse rounded-lg bg-gray-300"></div>
    );
  }

  if (type === 'card') {
    return (
      <div className="mr-4 w-full max-w-sm space-y-4 rounded-lg bg-white p-4 shadow-md">
        <div className="h-32 w-full animate-pulse rounded-lg bg-gray-300"></div>
        <div className="h-6 animate-pulse rounded bg-gray-300"></div>
        <div className="h-6 w-3/4 animate-pulse rounded bg-gray-300"></div>
      </div>
    );
  }

  return null;
};

const EmailComponent = ({ messageId, analytics, onClose, isOutlook }) => {
  const [isAnalyticsView, setIsAnalyticsView] = useState(false);
  const [email, setEmail] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAIViewButton, setShowAIViewButton] = useState(false);

  const [showAll, setShowAll] = useState(false);
  const visibleCount = 3;

  const { userGoogleId } = DataState();

  useEffect(() => {
    const setMessageDetails = async () => {
      if (!messageId) {
        return;
      }

      try {
        let message_data = null;
        let fromDB = true;

        // Try fetching from DB
        try {
          if (isOutlook) {
            message_data = await getOutlookEmailDetailsFromDB(
              messageId,
              userGoogleId,
            );
          } else {
            message_data = await getGmailEmailDetailsFromDB(
              messageId,
              userGoogleId,
            );
          }

          setShowAIViewButton(true); // Found in DB → show AI View
        } catch (err) {
          //If not found in DB (404), fallback to API
          fromDB = false;
          setShowAIViewButton(false); // Not in DB → hide AI View

          if (isOutlook) {
            message_data = await getOutlookMessageDetails(
              messageId,
              userGoogleId,
            );
          } else {
            message_data = await getGmailMessageDetails(
              messageId,
              userGoogleId,
            );
          }
        }

        if (fromDB) {
          message_data.snippet = message_data.body_preview || '';
          message_data.subject = message_data.subject || 'No Subject';
          message_data.body = message_data.body || '';
          message_data.date =
            message_data.date || message_data.created_date_time;
          message_data.from = [
            // convert sender to array
            {
              name: message_data.sender?.name || '',
              email: message_data.sender?.contact || '',
            },
          ];
          message_data.to = (message_data.recipients || []).map((to) => ({
            name: to.name,
            email: to.contact,
          }));
          message_data.attachments = message_data.attachments || [];
          if (isOutlook) {
            message_data.attachments = (message_data.attachments || []).map(
              (attachment) => {
                return {
                  ...attachment,
                  mimeType: attachment.contentType,
                  attachmentId: attachment.id,
                  filename: attachment.name,
                };
              },
            );
          }
        } else {
          if (isOutlook) {
            message_data.from.email = message_data.from?.address;
            message_data.from = [message_data.from];
            message_data.to = message_data.to?.map((to) => {
              return { ...to, email: to.address };
            });
            message_data.attachments = (message_data.attachments || []).map(
              (attachment) => {
                return {
                  ...attachment,
                  mimeType: attachment.contentType,
                  attachmentId: attachment.id,
                  filename: attachment.name,
                };
              },
            );
          } else if (!isOutlook) {
            // 🌟 Parse Gmail format
            message_data.snippet = message_data.snippet || '';
            message_data.body = message_data.body || '';
            message_data.from = parseEmailList(message_data.from);
            message_data.to = parseEmailList(message_data.to);
          }
        }

        const { time, date } = convertToIST(message_data.date);
        message_data.time = time;
        message_data.date = date;

        setEmail(message_data);
        setLoading(false);
      } catch (error) {
        toast.error(error);
        setLoading(false);
      }
    };
    setMessageDetails();
  }, [messageId, userGoogleId, isOutlook]);

  const toggleView = () => {
    setIsAnalyticsView(!isAnalyticsView);
  };

  return createPortal(
    // eslint-disable-next-line
    <div
      onClick={onClose}
      className="fixed inset-0 z-100 flex items-center justify-center bg-black bg-opacity-50 max-md:p-2"
    >
      {/* eslint-disable-next-line */}
      <div
        className="flex w-full rounded-lg bg-gray-100 p-4 max-md:h-full md:min-h-[300px] md:w-2/3 md:flex-col md:shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full flex-row md:mt-4">
          <div className="hidden px-4 pl-0 md:block">
            <IoPersonCircle className="text-gray-700" size={40} />
          </div>
          <div className="flex h-full w-full flex-col">
            <div className="mb-2 flex w-full flex-col justify-between md:flex-row md:items-center">
              {loading ? (
                <div className="mb-2 h-6 w-full animate-pulse rounded bg-gray-300 md:mb-0 md:w-96" />
              ) : (
                <h2 className="mb-2  text-lg font-semibold text-gray-900 md:mb-0 md:text-xl">
                  {isAnalyticsView
                    ? 'Email Analytics'
                    : email.subject || 'No Subject'}
                </h2>
              )}
              <div
                className={` flex flex-wrap items-center ${showAIViewButton ? 'max-md:justify-between' : 'max-md:justify-end'} gap-2 md:mr-4 md:gap-4 `}
              >
                {showAIViewButton && (
                  <button
                    className={`flex items-center justify-center rounded px-3 py-1 text-sm text-white transition ${
                      loading
                        ? 'h-6 w-14 animate-pulse bg-gray-300'
                        : 'bg-zinc-500 hover:bg-zinc-600'
                    }`}
                    onClick={toggleView}
                    disabled={loading}
                  >
                    {loading ? '' : isAnalyticsView ? 'View Email' : 'AI View'}
                  </button>
                )}

                <div className="flex items-center gap-2">
                  {/* {loading ? (
                    <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
                  ) : (
                    <button className="text-gray-500 hover:text-gray-700">
                      <LuPrinter size={20} />
                    </button>
                  )} */}

                  {loading ? (
                    <div className="h-6 w-6 animate-pulse rounded bg-gray-300"></div>
                  ) : (
                    <Link
                      target="_blank"
                      to={
                        isOutlook
                          ? email?.webLink
                          : `https://mail.google.com/mail/u/0/#all/${messageId}`
                      }
                    >
                      <button className="text-gray-500 hover:text-gray-700">
                        <FiExternalLink size={20} />
                      </button>
                    </Link>
                  )}

                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      setIsAnalyticsView(false);
                      onClose();
                    }}
                  >
                    <MdClose size={20} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-between md:flex-row">
              <div className="mb-4 flex text-gray-700 max-md:w-full  md:items-center">
                <div
                  className={`rounded px-4 pl-0 md:hidden ${loading && 'animate-pulse text-gray-800'}`}
                >
                  <div
                    className={`flex h-10  w-10 items-center justify-center rounded-full text-sm font-bold text-white ${loading ? 'bg-gray-300' : 'bg-gray-700'}`}
                  >
                    {email.from?.map((from, index) => {
                      return (
                        <span key={index}>{from.name[0].toUpperCase()}</span>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col">
                  {email.from?.map((from, index) => {
                    return (
                      <span key={index} className="font-semibold">
                        {from.name}{' '}
                        <span className="text-sm font-normal text-gray-500">
                          {from.email || 'No Sender'}
                        </span>
                      </span>
                    );
                  })}

                  <div className="flex">
                    {!loading && (
                      <span className="mr-2 text-gray-500"> to </span>
                    )}
                    <div className="flex items-center">
                      {email.to?.slice(0, visibleCount).map((to, index) => (
                        <span
                          key={index}
                          className="font-semibold"
                          title={to?.email || 'No Recipient'}
                        >
                          {to?.name || to?.email || 'No Recipient'}
                          {index <
                            Math.min(visibleCount, email.to?.length) - 1 &&
                            ', '}
                        </span>
                      ))}

                      {/* Toggle button to expand/collapse extra recipients */}
                      {email.to?.length > visibleCount && (
                        <button
                          className="ml-1 flex items-center text-[#5652CF] hover:underline"
                          onClick={() => setShowAll(!showAll)}
                        >
                          {showAll
                            ? 'show less'
                            : `+${email.to?.length - visibleCount}`}
                          {showAll ? (
                            <HiChevronUp size={14} />
                          ) : (
                            <HiChevronDown size={14} />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Extra emails, smoothly expanding below */}
                    <div
                      className={`rounded-xl border border-gray-400 p-2 font-semibold text-gray-500 transition-all duration-300 ease-in-out ${
                        showAll
                          ? 'max-h-40 opacity-100'
                          : 'max-h-0 overflow-hidden opacity-0'
                      }`}
                    >
                      {email.to?.slice(visibleCount).map((to, index) => (
                        <span
                          key={index}
                          className="block text-sm text-gray-700"
                        >
                          {to.name || to.email || 'No Recipient'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 text-sm max-md:w-full max-md:justify-between md:mr-4">
                <div className="flex gap-2">
                  <div className="mb-4 flex items-center gap-2">
                    {!loading ? (
                      <span className="text-sm text-gray-500">
                        {email.date || ''}
                      </span>
                    ) : (
                      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-500"></div>
                    )}
                  </div>
                  <div className="mb-4 flex items-center">
                    {!loading ? (
                      <span className="text-sm text-gray-500">
                        {email.time || ''}
                      </span>
                    ) : (
                      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-300"></div>
                    )}
                  </div>
                </div>

                {/* <div className="flex gap-2">
                  <button
                    className="text-gray-500 hover:text-gray-700 mb-4"
                  >
                    <FaRegStar />
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 mb-4"
                  >
                    <FiSmile />
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 mb-4"
                  >
                    <PiArrowBendUpLeftBold />
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 mb-4"
                  >
                    <BsThreeDotsVertical />
                  </button>
                </div> */}
              </div>
            </div>
            {isAnalyticsView ? (
              <AIEmailView email={email} />
            ) : (
              <div className="relative">
                {!loading && (
                  <button
                    className="absolute bottom-2 right-10 transform rounded-full bg-gray-200 p-2 text-gray-500 transition-all hover:scale-110 hover:bg-gray-300 hover:text-gray-700"
                    onClick={() => setIsFullScreen(true)}
                  >
                    <FiMaximize size={20} />
                  </button>
                )}
                {loading ? (
                  <div className="pr-10">
                    <SkeletonLoader type="emailbody" />
                  </div>
                ) : (
                  // <article
                  //   // dangerouslySetInnerHTML={{ __html: email.body }}
                  //   className="text-gray-700 mr-4 whitespace-pre-line max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200 [&_*]:m-0 [&_*]:p-0"
                  // > {/* </article> */}
                  <div className="max-h-96 flex-1 overflow-y-auto">
                    <EmailIframe emailBody={email.body}></EmailIframe>
                  </div>
                )}
              </div>
            )}
            <div className="mt-4">
              <div className="mb-2 flex items-center border-t border-gray-300 p-4 pl-0">
                <FiPaperclip className="mr-2 text-gray-500" size={20} />
                <span className="text-gray-500">Attachments</span>
              </div>
              {email.attachments && email.attachments.length > 0 && (
                <div className="flex min-h-[40px] flex-grow flex-wrap  items-center gap-2 p-1">
                  {email.attachments.map((att) => (
                    <AttachmentItem
                      key={att.attachmentId}
                      messageId={messageId}
                      attachment={att}
                      isOutlook={isOutlook}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isFullScreen && (
        <div className="fixed inset-0 z-50 h-screen w-screen bg-white p-6">
          {/* Close button stays fixed at top-right */}
          <button
            className="absolute bottom-8 right-14 transform rounded-full bg-gray-200 p-2 text-gray-500 transition-all hover:scale-110 hover:bg-gray-300 hover:text-gray-700"
            onClick={() => setIsFullScreen(false)}
          >
            <FiMinimize size={24} />
          </button>

          {/* Scrollable content */}
          <div className="scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-200 h-full overflow-auto">
            {/* <article
                dangerouslySetInnerHTML={{ __html: email.body }}
                className="text-gray-700 whitespace-pre-line"
              /> */}
            <EmailIframe emailBody={email.body}></EmailIframe>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default EmailComponent;
